import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate } from 'lib/functions/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { Dispatch } from 'redux';

import {
  getAutocompleteGlobalSearcher,
  preloadBusinessesGlobalSearcher,
} from '../../api/search';
import CustomButton from '../../designSystem/Button';
import MenuDialog from '../../designSystem/MenuDialog';
import DateFilterGroup from './components/DateFilterGroup';
import {
  getNonDateFilters,
  groupDateFilters,
  isAutocompleteFilter,
  isDateFilter,
  isLocalAutocompleteFilter,
  isMultipleFilter,
  isSelectFilter,
  isSingleDateFilter,
  isTextFilter,
  renderAutocompleteFilter,
  renderLocalAutocompleteFilter,
  renderMultipleFilter,
  renderSelectFilter,
  renderSingleDateFilter,
  renderTextFilter,
} from './filterHelpers';
import { AutocompleteOption, Filter } from './filterTypes';

dayjs.extend(utc);

const BUSINESS_PRELOAD_FIELDS = [
  'stakeholderName',
  'businessName',
  'businessOriginName',
  'businessDestinationName',
];

const differenceInMonths = (date1: Date, date2: Date): number => {
  const yearDiff = date2.getFullYear() - date1.getFullYear();
  const monthDiff = date2.getMonth() - date1.getMonth();
  return yearDiff * 12 + monthDiff;
};

export type { Filter } from './filterTypes';

interface SearchComponentProps {
  filters: Filter[];
  triggerSearch: (params?: any) => Promise<any>;
  pagination?: { pageSize: number };
  sortOptions?: { value: string; label: string }[];
  selectedSortBy?: string;
  setSelectedSortBy?: (key: string) => void;
  sortOrder?: 'asc' | 'desc';
  setSortOrder?: (order: 'asc' | 'desc') => void;
  dispatch?: Dispatch;
  disableSearch?: boolean;
  maxMonths?: number;
  shortcuts?: boolean;
  shortcutDirection?: 'past' | 'future';
  showActionButtons?: boolean;
}

const GlobalSearcher: React.FC<SearchComponentProps> = ({
  filters,
  triggerSearch,
  selectedSortBy,
  setSelectedSortBy,
  sortOptions,
  sortOrder,
  setSortOrder,
  dispatch,
  disableSearch,
  maxMonths,
  shortcuts = true,
  shortcutDirection = 'past',
  showActionButtons = true,
}) => {
  const [fakeValues, setFakeValues] = useState<{ [key: string]: string }>({});
  const [autocompleteOptions, setAutocompleteOptions] = useState<{
    [key: string]: AutocompleteOption[];
  }>({});
  const [autocompleteLoading, setAutocompleteLoading] = useState<{
    [key: string]: boolean;
  }>({});
  const [autocompleteInputValues, setAutocompleteInputValues] = useState<{
    [key: string]: string;
  }>({});
  const [autocompleteValues, setAutocompleteValues] = useState<{
    [key: string]: AutocompleteOption | null;
  }>({});
  const [debounceTimers, setDebounceTimers] = useState<{
    [key: string]: ReturnType<typeof setTimeout>;
  }>({});
  const [preloadedBusinesses, setPreloadedBusinesses] = useState<
    AutocompleteOption[]
  >([]);
  const [isPreloadingBusinesses, setIsPreloadingBusinesses] = useState(false);
  const [hasPreloadedBusinesses, setHasPreloadedBusinesses] = useState(false);

  const getAutocompleteOptions = async (
    label: string,
    input: string,
  ): Promise<AutocompleteOption[]> => {
    if (!dispatch) {
      console.error('Dispatch is required for autocomplete functionality');
      return [];
    }

    try {
      const response = await getAutocompleteGlobalSearcher(dispatch, {
        label,
        input,
      });
      return response || [];
    } catch (error) {
      console.error('Error fetching autocomplete options:', error);
      return [];
    }
  };

  const handleSearch = async () => {
    if (!dateValidations()) {
      return;
    }
    await triggerSearch();
  };

  const handleClearFilters = () => {
    filters.forEach((filter) => {
      if (filter.clearable === false) return;

      if (isDateFilter(filter)) {
        filter.setter(null);
      } else if (isSingleDateFilter(filter)) {
        filter.setter(null);
      } else if (isTextFilter(filter)) {
        filter.setter('');
      } else if (isSelectFilter(filter)) {
        filter.setter('');
      } else if (isMultipleFilter(filter)) {
        filter.setter([]);
      } else if (isAutocompleteFilter(filter)) {
        filter.setter(null);
        if (filter.setSelectedValue) {
          filter.setSelectedValue(null);
        }
      }
    });
    setFakeValues({});
    setAutocompleteValues({});
    setAutocompleteInputValues({});
  };

  const dateValidations = () => {
    const datePairs = groupDateFilters(filters);

    for (const pair of datePairs) {
      const { startFilter, endFilter } = pair;
      const startValue = startFilter.value;
      const endValue = endFilter.value;

      if (startValue && !endValue) {
        return false;
      }
      if (startValue && endValue && new Date(startValue) > new Date(endValue)) {
        return false;
      }
      if (maxMonths !== undefined && startValue && endValue) {
        const diffInMonths = differenceInMonths(
          new Date(startValue),
          new Date(endValue),
        );
        if (diffInMonths > maxMonths) {
          return false;
        }
      }
    }
    return true;
  };

  const handleChangeDateNormal = (
    value: string,
    type: string,
    dateKey?: string,
  ) => {
    if (!value) {
      if (type === 'start') {
        const startFilter = filters.find(
          (f) => isDateFilter(f) && f.dateKey === (dateKey || 'startDate'),
        );
        if (startFilter && isDateFilter(startFilter)) {
          startFilter.setter(null);
        }
      } else {
        const endFilter = filters.find(
          (f) => isDateFilter(f) && f.dateKey === (dateKey || 'endDate'),
        );
        if (endFilter && isDateFilter(endFilter)) {
          endFilter.setter(null);
        }
      }
      return;
    }

    if (type === 'start') {
      const formattedValue = formatDate(new Date(value));
      const startFilter = filters.find(
        (f) => isDateFilter(f) && f.dateKey === (dateKey || 'startDate'),
      );
      if (!startFilter || !isDateFilter(startFilter)) {
        console.error('No se encontró el setter para la fecha de inicio');
        return;
      }
      startFilter.setter(formattedValue);
    } else {
      const endDateWithTime = dayjs(value)
        .utc()
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

      const endFilter = filters.find(
        (f) => isDateFilter(f) && f.dateKey === (dateKey || 'endDate'),
      );
      if (!endFilter || !isDateFilter(endFilter)) {
        console.error('No se encontró el setter para la fecha de fin');
        return;
      }
      endFilter.setter(endDateWithTime);
    }
  };

  const handleChangeText = (
    value: string,
    setter: (value: string) => void,
    label: string,
    specialHandler?: string,
  ) => {
    if (specialHandler === 'id') {
      setFakeValues({
        ...fakeValues,
        [label]: value,
      });
      const valueWithoutP = value.replace('P', '').replace('p', '');
      setter(valueWithoutP);
    } else {
      setFakeValues({ ...fakeValues, [label]: value });
      setter(value);
    }
  };

  const loadPreloadedBusinesses = async () => {
    if (hasPreloadedBusinesses || isPreloadingBusinesses || !dispatch) return;

    setIsPreloadingBusinesses(true);
    try {
      const data = await preloadBusinessesGlobalSearcher(dispatch);
      if (data) {
        setPreloadedBusinesses(data);
        setHasPreloadedBusinesses(true);
      }
    } catch (error) {
      console.error('Error preloading businesses:', error);
    } finally {
      setIsPreloadingBusinesses(false);
    }
  };

  const filterPreloadedBusinesses = (inputValue: string) => {
    if (!inputValue.trim()) return [];
    const searchTerm = inputValue.toLowerCase();
    return preloadedBusinesses.filter(
      (item) =>
        item.label.toLowerCase().includes(searchTerm) ||
        item.value.toLowerCase().includes(searchTerm),
    );
  };

  const handleAutocompleteInputChange = useCallback(
    (label: string, inputValue: string, reason: string) => {
      const filter = filters.find((f) => f.label === label);
      if (!filter || !isAutocompleteFilter(filter) || !filter.key) return;

      setAutocompleteInputValues((prev) => ({
        ...prev,
        [label]: inputValue,
      }));

      if (reason === 'input') {
        const shouldUsePreloadedData = BUSINESS_PRELOAD_FIELDS.includes(
          filter.key,
        );

        if (
          shouldUsePreloadedData &&
          !hasPreloadedBusinesses &&
          !isPreloadingBusinesses
        ) {
          void loadPreloadedBusinesses();
        }

        if (debounceTimers[label]) {
          clearTimeout(debounceTimers[label]);
        }

        setAutocompleteLoading((prev) => ({
          ...prev,
          [label]: true,
        }));

        if (
          shouldUsePreloadedData &&
          inputValue.trim() &&
          hasPreloadedBusinesses
        ) {
          const filteredPreloaded = filterPreloadedBusinesses(inputValue);
          setAutocompleteOptions((prev) => ({
            ...prev,
            [label]: filteredPreloaded,
          }));
        }

        const timer = setTimeout(() => {
          void (async () => {
            try {
              if (inputValue.trim()) {
                if (!filter.key) return;
                const apiOptions = await getAutocompleteOptions(
                  filter.key,
                  inputValue,
                );

                if (shouldUsePreloadedData) {
                  const filteredPreloaded =
                    filterPreloadedBusinesses(inputValue);
                  const apiValues = new Set(apiOptions.map((opt) => opt.value));
                  const uniquePreloaded = filteredPreloaded.filter(
                    (item) => !apiValues.has(item.value),
                  );
                  const mergedOptions = [...uniquePreloaded, ...apiOptions];

                  setAutocompleteOptions((prev) => ({
                    ...prev,
                    [label]: mergedOptions,
                  }));
                } else {
                  setAutocompleteOptions((prev) => ({
                    ...prev,
                    [label]: apiOptions,
                  }));
                }
              } else {
                setAutocompleteOptions((prev) => ({
                  ...prev,
                  [label]: [],
                }));
              }
            } catch (error) {
              console.error('Error searching autocomplete options:', error);
              if (shouldUsePreloadedData && inputValue.trim()) {
                const filteredPreloaded = filterPreloadedBusinesses(inputValue);
                setAutocompleteOptions((prev) => ({
                  ...prev,
                  [label]: filteredPreloaded,
                }));
              } else {
                setAutocompleteOptions((prev) => ({
                  ...prev,
                  [label]: [],
                }));
              }
            } finally {
              setAutocompleteLoading((prev) => ({
                ...prev,
                [label]: false,
              }));
            }
          })();
        }, 500);

        setDebounceTimers((prev) => ({
          ...prev,
          [label]: timer,
        }));
      }
    },
    [
      filters,
      debounceTimers,
      dispatch,
      preloadedBusinesses,
      hasPreloadedBusinesses,
      isPreloadingBusinesses,
    ],
  );

  const handleAutocompleteChange = (
    label: string,
    value: AutocompleteOption | null,
  ) => {
    const filter = filters.find((f) => f.label === label);
    if (!filter || !isAutocompleteFilter(filter)) return;

    setAutocompleteValues((prev) => ({
      ...prev,
      [label]: value,
    }));

    if (filter.setter) {
      filter.setter(value?.value || null);
    }

    if (filter.setSelectedValue) {
      filter.setSelectedValue(value);
    }

    setAutocompleteInputValues((prev) => ({
      ...prev,
      [label]: value ? value.label : '',
    }));

    setFakeValues((prev) => ({
      ...prev,
      [label]: value?.label || '',
    }));
  };

  const setUpFakeValues = () => {
    const newFakeValues: { [key: string]: string } = {};
    filters.forEach((filter) => {
      if (filter.value) {
        if (isTextFilter(filter) && filter.specialHandler === 'id') {
          const value = `P${filter.value}`;
          newFakeValues[filter.label] = value;
        } else {
          newFakeValues[filter.label] = String(filter.value);
        }
      }
    });
    setFakeValues(newFakeValues);
  };

  useEffect(() => {
    setUpFakeValues();
  }, []);

  useEffect(() => {
    const newInputValues: { [key: string]: string } = {};
    const newAutocompleteValues: {
      [key: string]: AutocompleteOption | null;
    } = {};

    filters.forEach((filter) => {
      if (isAutocompleteFilter(filter)) {
        if (filter.selectedValue) {
          newInputValues[filter.label] = filter.selectedValue.label;
          newAutocompleteValues[filter.label] = filter.selectedValue;
        } else if (filter.value) {
          newAutocompleteValues[filter.label] = {
            value: filter.value,
            label: filter.value,
          };
          newInputValues[filter.label] = filter.value;
        }
      }
    });

    setAutocompleteInputValues(newInputValues);
    setAutocompleteValues(newAutocompleteValues);
  }, [filters]);

  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, [debounceTimers]);

  return (
    <MenuDialog buttonLabel="Buscar" buttonColor="secondary">
      <div
        style={{
          padding: '8px 16px',
          maxHeight: 600,
          overflowY: 'auto',
          maxWidth: 300,
        }}
      >
        <p style={{ fontSize: '1px', color: 'transparent' }}>_</p>
        {filters.some((f) => isDateFilter(f)) && (
          <DateFilterGroup
            datePairs={groupDateFilters(filters)}
            onDateChange={handleChangeDateNormal}
            showShortcuts={shortcuts}
            maxMonths={maxMonths}
            shortcutDirection={shortcutDirection}
          />
        )}
        {filters.some((f) => isSingleDateFilter(f)) && (
          <div style={{ marginBottom: 8 }}>
            <Typography variant="body1" fontWeight="bold" marginBottom={1}>
              Fechas
            </Typography>
            {filters
              .filter((f) => isSingleDateFilter(f))
              .map((filter, index) => {
                if (!isSingleDateFilter(filter)) return null;
                return (
                  <div key={index} style={{ padding: '5px 0px' }}>
                    {renderSingleDateFilter({
                      filter,
                      value: filter.value || '',
                      onChange: (value) => {
                        if (!value) {
                          filter.setter(null);
                        } else {
                          filter.setter(value);
                        }
                      },
                    })}
                  </div>
                );
              })}
          </div>
        )}
        {getNonDateFilters(filters).length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <Typography variant="body1" fontWeight="bold" marginBottom={1}>
              <span style={{ fontSize: '1px' }}>_</span>
              Filtros
            </Typography>
            {getNonDateFilters(filters).map((filter, index) => (
              <div key={index} style={{ padding: '5px 0px' }}>
                {isTextFilter(filter) &&
                  renderTextFilter({
                    filter,
                    value: fakeValues[filter.label] || '',
                    onChange: (value) =>
                      handleChangeText(
                        value,
                        filter.setter,
                        filter.label,
                        filter.specialHandler,
                      ),
                  })}
                {isSelectFilter(filter) &&
                  renderSelectFilter({
                    filter,
                    onChange: (value) => filter.setter(value),
                  })}
                {isMultipleFilter(filter) &&
                  renderMultipleFilter({
                    filter,
                    onChange: (value) => filter.setter(value),
                  })}
                {isAutocompleteFilter(filter) &&
                  renderAutocompleteFilter({
                    filter,
                    inputValue: autocompleteInputValues[filter.label] || '',
                    onInputChange: (value, reason) =>
                      handleAutocompleteInputChange(
                        filter.label,
                        value,
                        reason,
                      ),
                    onChange: (value) =>
                      handleAutocompleteChange(filter.label, value),
                    options: autocompleteOptions[filter.label] || [],
                    loading: autocompleteLoading[filter.label] || false,
                    value: autocompleteValues[filter.label] || null,
                  })}
                {isLocalAutocompleteFilter(filter) &&
                  renderLocalAutocompleteFilter({
                    filter,
                    value: filter.value || null,
                    onChange: (value) => filter.setter(value),
                    options:
                      filter?.options?.map((option) => option.label) || [],
                  })}
              </div>
            ))}
          </div>
        )}
        {sortOptions && (
          <div>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Ordenar
            </Typography>
            <div style={{ padding: '5px 0px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Por</InputLabel>
                <Select
                  value={selectedSortBy || ''}
                  onChange={(e) => setSelectedSortBy?.(e.target.value)}
                  label="Por"
                >
                  {sortOptions.map((option, idx) => (
                    <MenuItem key={idx} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div style={{ padding: '5px 0px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Orden</InputLabel>
                <Select
                  value={sortOrder || ''}
                  onChange={(e) =>
                    setSortOrder?.(e.target.value as 'asc' | 'desc')
                  }
                  label="Orden"
                >
                  <MenuItem value="asc">Ascendente</MenuItem>
                  <MenuItem value="desc">Descendente</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        )}

        {showActionButtons && (
          <div
            style={{
              padding: '10px 0px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              onClick={handleClearFilters}
              style={{ cursor: 'pointer', color: 'blue' }}
            >
              Limpiar filtros
            </Typography>
            <CustomButton
              color="primary"
              onClick={() => handleSearch()}
              disabled={disableSearch}
            >
              Buscar
            </CustomButton>
          </div>
        )}
      </div>
    </MenuDialog>
  );
};

export default GlobalSearcher;
